import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { trackSignup, type SignupSource } from "@/lib/trackSignup";

interface NewsletterSignupProps {
  /** Where this form lives — used for conversion attribution. */
  source?: SignupSource;
}

export const NewsletterSignup = ({ source = "home" }: NewsletterSignupProps = {}) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  const validateEmail = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return "Please enter your email address.";
    if (trimmed.length > 255) return "Email must be less than 255 characters.";
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!re.test(trimmed)) return "Please enter a valid email address.";
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validateEmail(email);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);

    trackSignup("newsletter_signup_attempt", source, email);
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke(
        "subscribe-newsletter",
        { body: { email: email.trim(), source } },
      );
      if (error || (data && (data as any).error)) {
        throw error ?? new Error((data as any).error);
      }
      const subscriberId = (data as { subscriberId?: string } | null)?.subscriberId ?? null;
      trackSignup("newsletter_signup_success", source, email, subscriberId);

      toast({
        title: "You're in.",
        description: "Welcome.",
      });
      setEmail("");
    } catch (err) {
      trackSignup("newsletter_signup_error", source, email);
      toast({
        title: "Something went wrong",
        description: "Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 bg-secondary relative">
      <div className="container mx-auto px-4">
        <div className="max-w-2xl mx-auto text-center space-y-6">
          <h2 id="newsletter-heading" className="font-display text-3xl md:text-4xl font-bold text-secondary-foreground">
            Stay <span className="text-accent">Honest</span>
          </h2>
          <p className="text-lg text-secondary-foreground/80">
            Occasional reflections and journal entries — delivered to your inbox. No spam. No programs. No self-improvement formulas. Just honest words from a man in the middle of it.
          </p>
          <form
            onSubmit={handleSubmit}
            aria-labelledby="newsletter-heading"
            noValidate
            className="max-w-md mx-auto"
          >
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1 text-left">
                <Label htmlFor="newsletter-email" className="sr-only">
                  Email address
                </Label>
                <Input
                  id="newsletter-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  placeholder="Your email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(null);
                  }}
                  required
                  aria-required="true"
                  aria-invalid={error ? "true" : "false"}
                  aria-describedby={error ? "newsletter-email-error" : undefined}
                  className="bg-secondary-foreground/10 border-secondary-foreground/20 text-secondary-foreground placeholder:text-secondary-foreground/50"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="bg-accent text-white hover:bg-accent/90 whitespace-nowrap"
              >
                {loading ? "Subscribing..." : "Subscribe"}
              </Button>
            </div>
            <p
              id="newsletter-email-error"
              role="alert"
              aria-live="polite"
              className={`mt-2 text-sm text-left text-destructive min-h-[1.25rem] ${error ? "" : "sr-only"}`}
            >
              {error ?? ""}
            </p>
            <p className="mt-4 text-sm text-secondary-foreground/70 text-center">
              One email when something real lands. Unsubscribe in one click. Your inbox stays yours.
            </p>
          </form>
        </div>
      </div>
    </section>
  );
};
